import React, { useState, useEffect } from 'react';
import { X, Users, AlertCircle, CheckCircle } from 'lucide-react';

const FlightBookingModal = ({ flight, onClose, language }) => {
  // Calculer le seuil de rentabilité initial
  const initialBreakeven = flight.breakeven_seats || 1;
  const currentSold = flight.seats_sold || 0;
  const initialMinSeats = currentSold < initialBreakeven ? initialBreakeven : 1;
  
  const [numSeats, setNumSeats] = useState(initialMinSeats);
  const [formData, setFormData] = useState({
    passenger_name: '',
    passenger_email: '',
    passenger_phone: '',
    member_card_number: localStorage.getItem('memberCardNumber') || '',
    notes: ''
  });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [minSeats, setMinSeats] = useState(initialMinSeats);

  useEffect(() => {
    fetchPricing();
  }, [numSeats]);
  
  useEffect(() => {
    if (minSeats > numSeats) {
      setNumSeats(minSeats);
    }
  }, [minSeats]);

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/member/flights/${flight.id}/calculate-price?num_seats=${numSeats}`
      );
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
        setMinSeats(data.minimum_seats_required || 1);
      } else {
        const err = await response.json();
        console.error('Pricing error:', err);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/flight-bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          flight_id: flight.id,
          num_seats: numSeats,
          ...formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        setTimeout(() => {
          onClose(true); // true = refresh needed
        }, 3000);
      } else {
        const err = await response.json();
        setError(err.detail || (language === 'fr' ? 'Erreur lors de la réservation' : 'Booking error'));
      }
    } catch (error) {
      setError(language === 'fr' ? 'Erreur lors de la réservation' : 'Booking error');
    } finally {
      setLoading(false);
    }
  };

  const available = flight.total_seats - flight.seats_sold;

  if (success) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'var(--bg-secondary)',
          maxWidth: '500px',
          width: '90%',
          padding: '48px',
          textAlign: 'center'
        }}>
          <CheckCircle size={64} color="#2c5f7d" style={{ margin: '0 auto 24px' }} />
          <h2 className="heading-2" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Réservation enregistrée !' : 'Booking registered!'}
          </h2>
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            {pricing && (pricing.breakeven_reached || pricing?.will_reach_breakeven)
              ? (language === 'fr' 
                  ? 'Votre réservation est confirmée. Nous vous contacterons bientôt pour finaliser les détails.'
                  : 'Your booking is confirmed. We will contact you soon to finalize the details.')
              : (language === 'fr'
                  ? 'Votre place est sur liste d\'attente. Vous serez informé de sa disponibilité dans les plus brefs délais.'
                  : 'Your seat is on the waiting list. You will be informed of its availability as soon as possible.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'var(--bg-secondary)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={() => onClose(false)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          <X size={24} />
        </button>

        <div style={{ padding: '32px' }}>
          <h2 className="heading-2" style={{ marginBottom: '24px' }}>
            {language === 'fr' ? 'Réserver ce vol' : 'Book this flight'}
          </h2>

          {/* Flight Info */}
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '8px' }}>{flight.title}</h3>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
              {flight.departure} → {flight.arrival} • {new Date(flight.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Number of Seats */}
            <div style={{ marginBottom: '24px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                {language === 'fr' ? 'Nombre de places *' : 'Number of seats *'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <select
                  value={numSeats}
                  onChange={(e) => setNumSeats(parseInt(e.target.value))}
                  required
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    cursor: 'pointer'
                  }}
                >
                  {[...Array(Math.max(1, Math.min(available, 10) - minSeats + 1))].map((_, i) => {
                    const seatNum = minSeats + i;
                    return (
                      <option key={seatNum} value={seatNum}>
                        {seatNum} {language === 'fr' ? 'place(s)' : 'seat(s)'}
                      </option>
                    );
                  })}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)' }}>
                  <Users size={16} />
                  <span className="body-small">{available} {language === 'fr' ? 'disponibles' : 'available'}</span>
                </div>
              </div>
            </div>

            {/* Pricing Display */}
            {pricing && (
              <div style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-light)',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span className="body-regular">
                    {language === 'fr' ? 'Prix unitaire' : 'Unit price'}
                    {pricing.is_early_bird && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        background: '#2c5f7d',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 600
                      }}>
                        EARLY BIRD
                      </span>
                    )}
                  </span>
                  <div>
                    {pricing.savings > 0 && (
                      <span style={{ 
                        textDecoration: 'line-through', 
                        color: 'var(--text-secondary)',
                        marginRight: '8px',
                        fontSize: '14px'
                      }}>
                        {pricing.original_price.toLocaleString('fr-FR')} €
                      </span>
                    )}
                    <span className="body-regular" style={{ fontWeight: 600 }}>
                      {pricing.unit_price.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                </div>
                {pricing.savings > 0 && (
                  <p className="body-small" style={{ color: '#2c5f7d', marginBottom: '8px' }}>
                    💰 {language === 'fr' ? 'Économie' : 'Savings'}: {pricing.savings.toLocaleString('fr-FR')} €
                  </p>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-light)'
                }}>
                  <span className="body-large" style={{ fontWeight: 600 }}>
                    {language === 'fr' ? 'Total' : 'Total'}
                  </span>
                  <span className="heading-3" style={{ color: '#2c5f7d' }}>
                    {pricing.total_price.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
            )}

            {/* Member Card */}
            <div style={{ marginBottom: '16px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                {language === 'fr' ? 'N° Carte Membre (optionnel)' : 'Member Card # (optional)'}
              </label>
              <input
                type="text"
                value={formData.member_card_number}
                onChange={(e) => setFormData(prev => ({ ...prev, member_card_number: e.target.value }))}
                placeholder={language === 'fr' ? 'Votre numéro de carte membre' : 'Your member card number'}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Passenger Info */}
            <div style={{ marginBottom: '16px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                {language === 'fr' ? 'Nom complet *' : 'Full name *'}
              </label>
              <input
                type="text"
                value={formData.passenger_name}
                onChange={(e) => setFormData(prev => ({ ...prev, passenger_name: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                Email *
              </label>
              <input
                type="email"
                value={formData.passenger_email}
                onChange={(e) => setFormData(prev => ({ ...prev, passenger_email: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                {language === 'fr' ? 'Téléphone *' : 'Phone *'}
              </label>
              <input
                type="tel"
                value={formData.passenger_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, passenger_phone: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label className="body-regular" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: 600 
              }}>
                {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows="3"
                placeholder={language === 'fr' ? 'Demandes spéciales, régime alimentaire...' : 'Special requests, dietary requirements...'}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading 
                ? (language === 'fr' ? 'Envoi en cours...' : 'Submitting...') 
                : (language === 'fr' ? 'Confirmer la réservation' : 'Confirm booking')}
            </button>

            {pricing && pricing.will_reach_breakeven && !pricing.breakeven_reached && (
              <p className="body-small" style={{ 
                marginTop: '12px', 
                textAlign: 'center',
                color: '#2c5f7d',
                fontWeight: 600
              }}>
                🎉 {language === 'fr' 
                  ? 'Votre réservation confirmera ce vol !'
                  : 'Your booking will confirm this flight!'}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default FlightBookingModal;
